import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useConfirm } from '../../lib/confirm'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Load, Truck } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, Input, Skeleton, StatusBadge } from '../../components/ui'

export default function LoadDetail() {
  const { loadId } = useParams<{ loadId: string }>()
  const { user } = useAuth()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [showVerify, setShowVerify] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'load', loadId],
    queryFn: () => api.get<{ ok: boolean; item: Load; truck: Truck | null }>(`/leader/loads/${loadId}`),
    enabled: Boolean(loadId),
  })

  const verify = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(`/leader/loads/${loadId}/verify`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'load', loadId] })
      void qc.invalidateQueries({ queryKey: ['leader', 'loads'] })
      setShowVerify(false)
    },
  })
  const cancel = useMutation({
    mutationFn: () => api.post(`/leader/loads/${loadId}/cancel`, { reason: 'Cancelled from portal' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'load', loadId] })
      void qc.invalidateQueries({ queryKey: ['leader', 'loads'] })
    },
  })

  if (!loadId) return <Navigate to="/loads" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/loads">Back to loads</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  const { item: load, truck } = data
  const isRecipient = (load.expected_recipient_uids ?? []).includes(user?.uid ?? '')
  const isDispatcher = load.dispatcher_uid === user?.uid
  const canAct = load.status !== 'delivered' && load.status !== 'cancelled'

  function onVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const actual = form.get('actual_tonnage')
    verify.mutate({ actual_tonnage: actual ? Number(actual) : undefined })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{titleCase(load.mineral_type) || 'Load'} · {formatNumber(load.tonnage, 1)} t</h1>
          <p className="page-head__sub">{[load.source_label, load.destination_label].filter(Boolean).join(' → ')}</p>
        </div>
        <StatusBadge status={load.status} />
      </div>

      <div className="grid grid--2" style={{ marginBottom: 'var(--sp-6)' }}>
        <Card>
          <CardHeader title="Load" />
          <CardBody>
            <div className="stack">
              <Row label="Dispatched" value={formatDate(load.created_at)} />
              <Row label="Tonnage" value={`${formatNumber(load.tonnage, 1)} t`} />
              {load.actual_tonnage != null && <Row label="Actual (delivered)" value={`${formatNumber(load.actual_tonnage, 1)} t`} />}
              <Row label="Buyer" value={load.buyer_name || '—'} />
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Truck" />
          <CardBody>
            {truck ? (
              <div className="stack">
                <Row label="Truck" value={`${truck.name} (${truck.plate})`} />
                <Row label="Driver" value={truck.driver_name || '—'} />
              </div>
            ) : (
              <p className="page-head__sub" style={{ margin: 0 }}>No truck details available.</p>
            )}
          </CardBody>
        </Card>
      </div>

      {canAct && (isRecipient || isDispatcher) && (
        <Card>
          <CardHeader title="Actions" />
          <CardBody>
            {(verify.isError || cancel.isError) && (
              <div style={{ marginBottom: 'var(--sp-3)' }}>
                <Alert tone="danger">
                  {verify.error instanceof ApiError ? verify.error.message
                    : cancel.error instanceof ApiError ? cancel.error.message : 'That action failed.'}
                </Alert>
              </div>
            )}
            {isRecipient && (
              showVerify ? (
                <form onSubmit={onVerify}>
                  <div className="row" style={{ alignItems: 'flex-end', gap: 'var(--sp-3)' }}>
                    <Input label="Actual tonnage (optional)" name="actual_tonnage" type="number" step="any" min="0" />
                    <Button type="submit" loading={verify.isPending}>Confirm delivery</Button>
                  </div>
                </form>
              ) : (
                <Button onClick={() => setShowVerify(true)} style={{ marginRight: 'var(--sp-3)' }}>
                  Mark as delivered
                </Button>
              )
            )}
            {isDispatcher && (
              <Button
                variant="danger"
                loading={cancel.isPending}
                onClick={async () => {
                  if (await confirm({ title: 'Cancel this dispatch?', destructive: true })) cancel.mutate()
                }}
              >
                Cancel dispatch
              </Button>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 550 }}>{value}</span>
    </div>
  )
}
